/** @format */

import { useEffect, useState } from "react";
import { assetUrl } from "fusion-core";
import { useForm, FormProvider } from "react-hook-form";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStep from "../enums/QuoteStep";
import TqClaimModal from "../components/tqClaimModal";
import TqClaimMapper from "../mappers/tqClaimMapper";
import { handleNext } from "../../../shared/utils/navigationUtils";
import { fetchVehicleClaim } from "../../../shared/services/simpleListItemsService";
import { useNavigate } from "fusion-plugin-react-router";
import {
  saveQuote,
  fetchPanelData,
  fetchPolicyDetails,
} from "../services/quoteService";
import useQuoteStore from "../store/QuoteStore";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { deformatDateT } from "../../../shared/utils/formatters";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";

const TqClaim = () => {
  const STATE = useAppStateStore();

  const methods = useForm();
  const { setValue } = methods;
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [claimTypes, setClaimTypes] = useState([]);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const maxClaimsAllowed = 10;

  const bars = assetUrl("../../../assets/icons/bars.svg");
  const trash = assetUrl("../../../assets/icons/trash-filled.svg");
  const pencil = assetUrl("../../../assets/icons/Group22.svg");

  // Fetch claims from store
  const fetchClaims = () => {
    const storeClaims = TqClaimMapper.get();
    setClaims(storeClaims);
  };

  // Get claim type description
  const getClaimDescription = (typeId) => {
    const claimType = claimTypes.find((type) => type.listItemId === typeId);
    return claimType?.listItemDescription || "";
  };

  useEffect(() => {
    fetchClaims();
    // Fetch claim types
    fetchVehicleClaim().then((types) => setClaimTypes(types));
  }, []);

  const handleDelete = (index) => {
    TqClaimMapper.delete(index);
    fetchClaims();
  };

  const handleClaimEdit = (claim, index) => {
    setClaimModalOpen(true);
    setValue("claimIndex", index);
    setValue("claimType", {
      value: claim.typeId,
      label: getClaimDescription(claim.typeId),
    });
    setValue("claimCost", claim.cost);
    setValue("WhosFault", claim.atFault ? "Yes" : "No");
    setValue("Injured", claim.injuryInvolved ? "Yes" : "No");
    setValue("AddClaim_Date", deformatDateT(claim.claimDate));
  };

  const handleAddClaim = () => {
    methods.reset({
      claimIndex: null,
      claimType: null,
      claimCost: "",
      Injured: null,
      WhosFault: null,
      AddClaim_Date: null,
    });

    if (claims.length < maxClaimsAllowed) {
      setClaimModalOpen(true);
    }
  };

  useEffect(() => {
    if (claimModalOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }

    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [claimModalOpen]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQCLAIM_ENTERED);
  }, []);

  const onClick = () => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQCLAIM_NEXT_CLICKED);
    const latestAdditionalDriver = useAppStateStore.getState().additionalDriver;
    if (latestAdditionalDriver === "Yes") {
      handleNext(QuoteStep.TQ_ADD_DRIVER);
    } else {
      const quoteData = useQuoteStore.getState().quote;
      saveQuote(quoteData, navigate).then((data) => {
        fetchPolicyDetails().then(() => {
          fetchPanelData(null, data.risk.riskObjectId).then(() => {
            handleNext(QuoteStep.TQ_PANEL);
          });
        });
      });
      handleNext(QuoteStep.TQ_PANEL_LOADER);
    }
  };

  return (
    <FormProvider {...methods}>
      <QuoteFormWrapper>
        <h1>Claims History</h1>
        <p>
          It's essential to disclose all claims to ensure we provide you with
          the most accurate quote.
        </p>

        <label className="labelName">
          Have you had any accidents, losses, thefts, fire, flood or claims in
          any vehicle regardless of fault or whether you claimed or not in the
          last 5 years?
        </label>

        <div className="fields">
          <div className="driverFields">
            {claims.length > 0 &&
              claims.map((claim, index) => (
                <div className="addDriverDiv" key={index}>
                  <div className="divimgname">
                    <img className="imgDesign" src={bars} alt="" />
                    <h2 className="divname">
                      {getClaimDescription(claim.typeId)}
                    </h2>
                    <h2 className="divname">
                      {deformatDateT(claim.claimDate)}
                    </h2>
                  </div>
                  <div className="btnimagestyle">
                    <button
                      type="button"
                      onClick={() => handleClaimEdit(claim, index)}
                      className="imgDim btnpencil"
                    >
                      <img className="imgDesign1" src={pencil} alt="" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(index)}
                      className="imgDim"
                    >
                      <img className="imgDesign2" src={trash} alt="" />
                    </button>
                  </div>
                </div>
              ))}
            {claims.length < maxClaimsAllowed && (
              <div className="vertical-line">
                <div className="addDrivers">
                  <button
                    type="button"
                    className="lookupBtn bg-gray"
                    onClick={handleAddClaim}
                  >
                    <strong>Add a Claim</strong>
                  </button>
                </div>
              </div>
            )}

            {claims.length == maxClaimsAllowed && (
              <p className="errorField claim-error">
                Your Claim Limit is Reached
              </p>
            )}
          </div>
        </div>
      </QuoteFormWrapper>
      <FormNavigationButtons
        type="button"
        prevStep={QuoteStep.TQ6}
        onClick={onClick}
        isValid={claims.length > 0}
      />

      <TqClaimModal
        onClaimSaved={fetchClaims}
        isModalOpen={claimModalOpen}
        setIsModalOpen={setClaimModalOpen}
      />
    </FormProvider>
  );
};

export default TqClaim;
